import { Injectable } from '@angular/core';
import { AuthService, RoutingService, WindowRef } from '@spartacus/core';
import { filter, Observable, of } from 'rxjs';
import { ModalService } from '../../../../../../../components/modal-list/modal-list.service';
import { SharedSelectTableConfirmationComponent } from '../shared-selection-table/close-shared-selection-table-confirmation/close-confirmation.component';
import { SoldToModalComponent } from '../sold-to-modal/sold-to-modal.component';
import { LocalStorageService } from '../../../../../../../core/services/localStorage.service';
import { AccountSelectionAdapter } from './account-selection.adapter';
import { Store } from '@ngrx/store';
import { accountSelector } from './store/account-selection.selectors';
import { loadAccountSelection, setSelectedAccount, setSelectedAddress } from './store/account-selection.actions';
import { ToasterService } from '../../../../../../../components/toaster/toaster.service';
import { ShipToModalComponent } from '../ship-to-modal/ship-to-modal.component';

export interface SelectedAccounts {
  displayAccount: string;
  displayAddress: string;
}
@Injectable({
  providedIn: 'root',
})
export class AccountSelectionService {
  private isLoggedIn = false;

  constructor(
    private authService: AuthService,
    private routing: RoutingService,
    private modalService: ModalService,
    private localStorageService: LocalStorageService,
    private accountAdapter: AccountSelectionAdapter,
    private store: Store,
    private winRef: WindowRef,
    private toasterService: ToasterService,
  ) {}

  init(): void {
    this.winRef.isBrowser() &&
      this.authService
        .isUserLoggedIn()
        .pipe(filter(isLoggedIn => isLoggedIn !== this.isLoggedIn))
        .subscribe(isLoggedIn => {
          this.isLoggedIn = isLoggedIn;

          if (this.isLoggedIn) {
            this.handleLogin();
          } else {
            this.handleLogout();
          }
        });
  }

  fetch() {
    this.store.dispatch(loadAccountSelection());
  }

  dispatchSelectedAccount(displayAccount: string): void {
    this.store.dispatch(setSelectedAccount({ displayAccount }));
  }

  dispatchSelectedAddress(displayAddress: string): void {
    this.store.dispatch(setSelectedAddress({ displayAddress }));
  }

  get(): Observable<SelectedAccounts> {
    return this.store.select(accountSelector);
  }

  private handleLogout(): void {
    this.clearLocalStorage();
    this.clearSelectedBackup();
    this.closeAllModals();
  }

  private handleLogin(): void {
    this.getDefaultAccounts();
    this.handleRemovedCartMessage();
  }

  private clearLocalStorage(): void {
    this.localStorageService.removeItem('selectedAddress');
    this.localStorageService.removeItem('selectedAccount');
  }

  private openSoldToModalIfNeeded(): void {
    if (!this.getAccountJson()) {
      this.openSelectionModal(SoldToModalComponent, 'sold-to-modal', true);
    }
  }

  private openShipToModalIfNeeded(): void {
    const backupAddres = this.localStorageService.getItem('backupAddress');
    const backupAccount = this.localStorageService.getItem('backupAccount');
    if (!this.getAddressJson()) {
      this.getUserAddresByLocalStorageSelectedAccount().subscribe(accounts => {
        if (accounts.length === 1) {
          this.setAddress(accounts[0]);
        } else {
          if (backupAccount && backupAddres) {
            this.openSelectionModal(ShipToModalComponent, 'ship-to-modal', false);
          } else {
            this.openSelectionModal(ShipToModalComponent, 'ship-to-modal', true);
          }
        }
      });
    }
  }

  private getDefaultAccounts() {
    if (!this.getAccountJson() || !this.getAddressJson()) {
      this.getCurrent().subscribe(orgUnit => {
        if (orgUnit?.defaultShipTo?.uid) {
          this.setAddress(orgUnit.defaultShipTo);
        }

        if (orgUnit?.defaultAccount?.uid) {
          this.setAccount(orgUnit.defaultAccount);
        } else {
          if (!this.getAccountJson()) {
            this.openSoldToModalIfNeeded();
          }
        }

        if (this.getAccountJson() && !this.getAddressJson()) {
          this.openShipToModalIfNeeded();
        }
      });
    }
  }

  public openSelectionModal(component: any, id: string, isLogin: boolean): void {
    this.modalService.create({
      id,
      title: '',
      width: 1000,
      component,
      data: {
        isLogin: isLogin,
      },
      onSkip: () => this.skipSelectionModal(isLogin),
    });
  }

  skipSelectionModal(isLogin: boolean) {
    if (isLogin) {
      this.modalService.create({
        id: 'shared-selection-table-close-confirmation',
        title: '',
        width: 500,
        hasCloseButton: false,
        component: SharedSelectTableConfirmationComponent,
      });
    } else {
      this.restoreSelectedBackup();
      this.modalService.close();
    }
  }

  private closeAllModals(): void {
    this.modalService.close([
      'shared-selection-table-modal',
      'shared-selection-table-close-confirmation',
      'sold-to-modal',
      'ship-to-modal',
    ]);
  }

  getCurrent(): Observable<any> {
    if (this.winRef.isBrowser()) {
      return this.accountAdapter.getCurrent();
    } else {
      return of();
    }
  }

  getAccountUsers(): Observable<any> {
    return this.accountAdapter.getAccountUsers();
  }

  getUserAddresByLocalStorageSelectedAccount(): Observable<any> {
    const selectedAccountJSON: any = this.getAccountJson();
    const selectedAccount = JSON.parse(selectedAccountJSON);
    return this.accountAdapter.getAddressUsers(selectedAccount?.uid);
  }

  setAddress(address: any) {
    this.localStorageService.setItem('selectedAddress', JSON.stringify(address));
    this.dispatchSelectedAddress(address?.displayAddress);
    if (this.winRef.isBrowser()) {
      window.location.reload();
    }
  }

  setAccount(account: any) {
    this.localStorageService.setItem('selectedAccount', JSON.stringify(account));
    this.localStorageService.setItem('cacheWarmup', 'true');
    this.dispatchSelectedAccount(account?.displayName);
  }

  getAccountJson() {
    return this.localStorageService.getItem('selectedAccount');
  }

  getAddressJson() {
    return this.localStorageService.getItem('selectedAddress');
  }

  isCacheWarmup() {
    return this.localStorageService.getItem('cacheWarmup') === 'true';
  }

  handleSelectedBackup() {
    const backupAddres = this.getAddressJson();
    const backupAccount = this.getAccountJson();
    if (backupAddres) {
      this.localStorageService.setItem('backupAddress', backupAddres);
    }
    if (backupAccount) {
      this.localStorageService.setItem('backupAccount', backupAccount);
    }
  }

  restoreSelectedBackup() {
    const backupAddres = this.localStorageService.getItem('backupAddress');
    const backupAccount = this.localStorageService.getItem('backupAccount');
    if (backupAccount) {
      const account = JSON.parse(backupAccount);
      this.setAccount(account);
    }
    if (backupAddres) {
      const address = JSON.parse(backupAddres);
      this.setAddress(address);
    }

    this.clearSelectedBackup();
  }

  clearSelectedBackup() {
    this.localStorageService.removeItem('backupAddress');
    this.localStorageService.removeItem('backupAccount');
  }

  setRemovedCartMessage() {
    this.localStorageService.setItem('cartRemoved', JSON.stringify(true));
  }

  handleRemovedCartMessage() {
    const isCartRemoved = this.localStorageService.getItem('cartRemoved');
    if (isCartRemoved) {
      this.localStorageService.removeItem('cartRemoved');
    }
  }
}
